import * as maptalks from "maptalks"
import range from "lodash/range"
import { BaseObject } from "maptalks.three"
import { Texture, MeshPhongMaterial, PlaneGeometry } from "three"
import { largestRect } from "d3plus-shape"
import { max, merge, isNumber, isArray } from "lodash"

const OPTIONS = {
  // Allowing click through and prevent interaction
  interactive: false,
  altitude: 0,
}

const defaultFlatLabelOptions = {
  fontSize: 14,
  fontFamily: "Manrope",
  fontWeight: 600,
  margin: 0,
  scaleMin: 0.5,
  lineHeight: 1.05,
  scaleStep: 0.05,
  textAlign: "center",
  textBaseline: "middle",
  fillStyle: "#000",
}

const defaultRectAngleToCalc = range(-90, 92, 2)

export const getMaterial = (text, flatLabelOptions) => {
  const options = merge({}, defaultFlatLabelOptions, flatLabelOptions)

  const {
    fontSize: initialFontSize,
    fontFamily,
    fontWeight,
    margin,
    scaleMin,
    scaleStep,
    fillStyle,
    lineHeight,
    textAlign,
    strokeStyle,
    lineWidth,
    textBaseline,
  } = options

  const pixelMultiplier = 4
  const SIZE = 100 * pixelMultiplier
  const fontSize = initialFontSize * (pixelMultiplier * 1.25)
  const canvas = document.createElement("canvas")
  canvas.width = canvas.height = SIZE
  const ctx = canvas.getContext("2d")

  ctx.font = `${fontWeight} ${fontSize}px "${fontFamily}", Arial`
  ctx.textAlign = textAlign
  ctx.textBaseline = textBaseline
  ctx.fillStyle = fillStyle
  ctx.strokeStyle = strokeStyle
  ctx.lineWidth = lineWidth

  // Function to wrap text automatically
  const wrapText = (ctx, text, maxWidth) => {
    const words = text.trim().split(/\s+/)
    if (words.length <= 1) return [text]

    const lines = []
    const MAX_LINES = 4
    let currentLine = words[0]

    for (let i = 1; i < words.length; i++) {
      const lineToMeasure = currentLine + " " + words[i]
      if (ctx.measureText(lineToMeasure).width > maxWidth) {
        lines.push(currentLine)
        currentLine = words[i]
      } else {
        currentLine = lineToMeasure
      }
    }
    lines.push(currentLine)
    return lines.slice(0, MAX_LINES) // Limit to 4 lines
  }

  /** Check if text contains manual line breaks */
  const hasManualBreaks = text.includes("\n")
  let texts

  if (hasManualBreaks) {
    // Use manual line breaks
    texts = text.split(/\n/g)
  } else {
    // Auto-wrap text
    const maxWidth = SIZE - 2 * margin
    texts = wrapText(ctx, text, maxWidth)
  }

  /** Automatically recalculate font size to fit the bound */
  let textWidth = max(texts.map((text) => ctx.measureText(text).width))
  let scale = 1
  while (scale > 0 && textWidth + 2 * margin > SIZE) {
    scale -= scaleStep
    ctx.font = `${fontWeight} ${scale * fontSize}px "${fontFamily}", Arial`
    textWidth = max(texts.map((text) => ctx.measureText(text).width))
  }

  // If the text is larger than scaleMin, then render it
  const center = { x: 0.5 * SIZE, y: 0.5 * SIZE }

  if (scale > scaleMin) {
    const totalHeight = texts.length * (fontSize * scale * lineHeight)
    const startY =
      center.y - totalHeight / 2 + fontSize * scale * lineHeight * 0.5 // Adjust startY when offset multi text line

    texts.forEach((text, index) => {
      const yOffset = startY + index * (fontSize * scale * lineHeight)
      if (strokeStyle && lineWidth) {
        ctx.strokeText(text, center.x, yOffset) // Draw outlined text
      }
      ctx.fillText(text, center.x, yOffset)
    })
  }

  const texture = new Texture(canvas)
  texture.needsUpdate = true

  const material = new MeshPhongMaterial({
    map: texture,
    transparent: true,
    // @ref: https://threejs.org/docs/#api/en/materials/Material.alphaTest
    alphaTest: 0.3,
  })
  return material
}

export class GroundLabel extends BaseObject {
  #angle = 0
  #bearing = 0
  #text = ""
  #offsetX = 0
  #offsetY = 0
  #originalPosition = null
  #layer = null

  constructor(bound, options, layer) {
    options = maptalks.Util.extend({}, OPTIONS, options, {
      layer,
      coordinate: bound,
    })

    const {
      altitude,
      text,
      fontSize,
      fillStyle,
      textAlign,
      fontFamily,
      textBaseline,
      strokeStyle,
      lineWidth,
      angle = defaultRectAngleToCalc,
      maxFontScale,
      offsetX = 0,
      offsetY = 0,
      ...properties
    } = options

    super()
    this._initOptions(options)
    this.properties = properties

    this.#offsetX = offsetX
    this.#offsetY = offsetY
    this.#layer = layer

    const material = getMaterial(text, {
      fillStyle,
      fontSize,
      textAlign,
      textBaseline,
      fontFamily,
      strokeStyle,
      lineWidth,
    })

    // Convert angle to array if it's a single number
    const rectAngles = isArray(angle) ? angle : [angle]

    material.needsUpdate = true

    const rect = largestRect(bound, {
      cache: true,
      /**
       * Black magic here:
       * For some reason if we allow angle -90 or 90, some polygon will use that angle even if it's wrong angle to use.
       * So we remove -90 and 90 from choices, and use -85 & 85 instead.
       */
      angle: rectAngles,
    })

    const { cx, cy, width, angle: calculatedAngle } = rect
    this.#text = text
    this.#angle = calculatedAngle // Always use calculated angle from largestRect

    const geometry = new PlaneGeometry(1, 1)
    this._createMesh(geometry, material)

    // set object3d position
    const z = layer.altitudeToVector3(altitude, altitude).x
    const basePosition = layer.coordinateToVector3({ x: cx, y: cy }, z)

    this.#originalPosition = basePosition.clone()
    const finalPosition = this.#calculateFinalPosition(basePosition)

    const scale = width / 0.0006456122659
    // check if it has maxFontScaleLimit from option use maxFontScale
    /* 
      Todo 
      - rename maxFontScale 
      - Refactor scale logic (starting from large and gradually scaling down)
    */
    const finalScale =
      maxFontScale && scale > maxFontScale ? maxFontScale : scale
    this.getObject3d().scale.set(finalScale, finalScale, finalScale)
    this.getObject3d().position.copy(finalPosition)
    this.getObject3d().rotation.z = (Math.PI / 180) * this.#angle
  }

  #calculateFinalPosition(basePosition) {
    if (this.#offsetX === 0 && this.#offsetY === 0) {
      return basePosition
    }

    const offsetCoordinate = {
      x: this.#offsetX,
      y: this.#offsetY,
    }

    const z = this.#layer.altitudeToVector3(0, 0).x
    const offsetVector = this.#layer.coordinateToVector3(offsetCoordinate, z)
    const zeroVector = this.#layer.coordinateToVector3({ x: 0, y: 0 }, z)

    const worldOffsetX = offsetVector.x - zeroVector.x
    const worldOffsetY = offsetVector.y - zeroVector.y

    return {
      x: basePosition.x + worldOffsetX,
      y: basePosition.y + worldOffsetY,
      z: basePosition.z,
    }
  }

  #updatePosition() {
    if (this.#originalPosition && this.#layer) {
      const finalPosition = this.#calculateFinalPosition(this.#originalPosition)
      this.getObject3d().position.copy(finalPosition)
    }
  }

  set bearing(value) {
    this.#bearing = value
    const degree = this.#angle + this.#bearing
    const angle = degree > 90 || degree < -90 ? this.#angle + 180 : this.#angle
    this.getObject3d().rotation.z = (Math.PI / 180) * angle
  }

  get angle() {
    return this.#angle
  }

  get currentAngle() {
    return this.#angle
  }

  get text() {
    return this.#text
  }

  get offsetX() {
    return this.#offsetX
  }

  get offsetY() {
    return this.#offsetY
  }

  get offset() {
    return { x: this.#offsetX, y: this.#offsetY }
  }

  set offsetX(value) {
    if (isNumber(value)) {
      this.#offsetX = value
      this.#updatePosition()
    }
  }

  set offsetY(value) {
    if (isNumber(value)) {
      this.#offsetY = value
      this.#updatePosition()
    }
  }

  set angle(newAngle) {
    if (isNumber(newAngle)) {
      this.#angle = newAngle
      this.getObject3d().rotation.z = (Math.PI / 180) * this.#angle
    }
  }

  setOffset(offsetX, offsetY) {
    if (isNumber(offsetX) && isNumber(offsetY)) {
      this.#offsetX = offsetX
      this.#offsetY = offsetY
      this.#updatePosition()
    }
  }

  addOffset(deltaX, deltaY) {
    if (isNumber(deltaX) && isNumber(deltaY)) {
      this.#offsetX += deltaX
      this.#offsetY += deltaY
      this.#updatePosition()
    }
  }

  resetOffset() {
    this.#offsetX = 0
    this.#offsetY = 0
    this.#updatePosition()
  }

  moveToPosition(targetX, targetY) {
    if (this.#originalPosition && this.#layer) {
      const currentCenter = this.#layer.vector3ToCoordinate(
        this.#originalPosition
      )
      this.#offsetX = targetX - currentCenter.x
      this.#offsetY = targetY - currentCenter.y
      this.#updatePosition()
    }
  }

  updateText(newText, options = {}) {
    this.#text = newText

    const materialOptions = {
      fillStyle: options.fillStyle || this.properties.fillStyle,
      fontSize: options.fontSize || this.properties.fontSize,
      textAlign: options.textAlign || this.properties.textAlign,
      textBaseline: options.textBaseline || this.properties.textBaseline,
      fontFamily: options.fontFamily || this.properties.fontFamily,
      strokeStyle: options.strokeStyle || this.properties.strokeStyle,
      lineWidth: options.lineWidth || this.properties.lineWidth,
    }

    const newMaterial = getMaterial(newText, materialOptions)
    this.getObject3d().material = newMaterial
    newMaterial.needsUpdate = true
  }
}
